// Department presets — pre-fill capability bundles when an admin assigns a
// user to a department. The underlying gating still flows through
// hasCapability (see utils/capabilities.js); presets just save the admin
// from clicking 20 individual toggles per new user.
//
// Per [[project-departments-as-capability-presets]]: department is UX only,
// capabilities remain the source of truth. Admin can tick/untick anything
// after the preset is applied. Re-assigning a department prompts and then
// reopens the permissions panel with the new preset already merged so the
// admin can tune before save.
//
// Preset values are EXPLICIT booleans for the caps that should differ from
// role-tier defaults:
//   true  → grant (meaningful for tech-tier users where the cap default is OFF)
//   false → revoke (meaningful for manager-tier users where the cap default is ON)
//   omitted → don't override (role-tier default applies)
//
// Phase 1 list locked 2026-06-15 (Owner / Billing / Dispatch / Service / Field Tech).
// New caps that ship later (markPaid, voidBills, viewARAging, etc. per
// [[project-finance-ar-module]]) get added to the relevant preset(s) when
// their feature lands.

// Departments are only meaningful for non-admin users. Admin role
// already grants everything (hasCapability returns true unconditionally
// for admin/executive), so "Owner" as a department was redundant and
// confusing — assigning it to a non-admin user did grant all caps, but
// assigning it to an admin user looked identical to assigning anything
// else (display masked by admin override). Removed 2026-06-15 after
// Justin caught the conflict.
export const DEPARTMENT_KEYS = [
  "billing",
  "dispatch",
  "service",
  "field_tech",
];

export const DEPARTMENT_LABELS = {
  billing: "Billing / Office",
  dispatch: "Dispatch",
  service: "Service Lead",
  field_tech: "Field Tech",
};

export const DEPARTMENT_DESCRIPTIONS = {
  billing:
    "Money + customer side. Read-only on jobs/quotes; full access to invoices, POs, time approval, recent activity.",
  dispatch:
    "Create + assign jobs, see all tickets and POs, approve time. Doesn't touch billing or customer records.",
  service:
    "Senior tech / service lead. Sees all jobs + tickets across the shop, but doesn't dispatch or bill.",
  field_tech:
    "Day-to-day tech defaults — only sees what they're assigned or created. No extra caps.",
};

// Each preset is a Record<capKey, boolean>. Only keys that differ from
// the user's role-tier default need to be listed. For tech users, omitted
// keys stay default-OFF; for manager users, omitted keys stay default-ON.
//
// "owner" is exhaustive (all caps true) so it grants everything regardless
// of underlying role tier — useful when an admin wants to wear that hat
// without giving them the admin role itself.
export const DEPARTMENT_PRESETS = {
  billing: {
    // Read-only on customer/job/equipment edits — billing shouldn't be
    // editing job records, that's dispatch/service work.
    editJobs: false,
    deleteJobs: false,
    deleteCustomers: false,
    deleteLocations: false,
    deleteEquipment: false,
    // Full visibility into the money side + time approval for payroll.
    createPOs: true,
    approveTime: true,
    viewAllQuotes: true,
    viewAllPOs: true,
    viewAllServiceTickets: true,
    viewRecentActivity: true,
    // Vendor master ownership lives with billing (1099 prep, W-9 collection).
    manageVendors: true,
    // Vendor bill verification is the core AP workflow (Phase A2).
    verifyVendorBills: true,
    // Voiding invoices is daily-life billing (Phase A) — wrong amount,
    // wrong customer, etc.
    voidBills: true,
    // Recording customer payments (AR) + approving + paying vendor bills
    // (AP) are the core billing-clerk loop (Phase B).
    markPaid: true,
    approvePayment: true,
    // Aging + history views are the day-to-day reporting Billing lives in
    // (Phase C).
    viewARAging: true,
    viewAPAging: true,
    viewPaymentHistory: true,
    // Margin reporting (Phase E) is sensitive — owners want to see
    // realized P&L. Billing dept is the right home for this.
    viewMargin: true,
    // Closed Bills archive — billing folks need to re-export old
    // invoices to customers.
    viewClosedBills: true,
    // Closed Jobs queue — closed jobs that haven't been billed yet.
    // Billing's actionable inbox; primary work surface.
    viewClosedJobs: true,
    // editBills is the billing-scoped write cap — separates "edit the
    // BILL on a job" (billing's core work: line items, finalize,
    // reopen) from "edit the JOB record" (dispatch / service work:
    // assign techs, change customer, edit description). Billing has
    // it on; editJobs stays off.
    editBills: true,
    // Invoices list — billing is the only role that needs the open
    // invoices view (dispatch / service / field tech don't deal with
    // money). Admin still sees everything via role override.
    viewInvoices: true,
  },
  dispatch: {
    // Job orchestration — create, edit, assign. No deletions.
    createJobs: true,
    editJobs: true,
    deleteJobs: false,
    // Visibility across the shop's tickets + POs to dispatch sensibly.
    createPOs: true,
    viewAllPOs: true,
    viewAllServiceTickets: true,
    viewRecentActivity: true,
    // Time approval for the techs they dispatch.
    approveTime: true,
  },
  service: {
    // Senior tech / service lead — broader visibility but doesn't edit job
    // records (that's dispatch).
    viewAllServiceTickets: true,
    viewAllPOs: true,
    viewRecentActivity: true,
  },
  field_tech: {
    // Day-to-day tech — no overrides. Whatever the tech role-tier default
    // is. Sees just their own tickets / assigned jobs.
  },
};

// Apply a department preset to a user's capabilities. Returns a NEW
// capabilities object — caller persists it.
//
// REPLACE semantics, not merge (decision 2026-06-15 after Justin tested
// switching between departments and saw no visible change). Each
// department selection produces the same result as a brand-new user
// assigned to that department — so admins can "bounce between" presets
// to compare them and pick the right starting point.
//
// Custom per-cap tweaks happen AFTER picking the department, not
// before. The transition UX (confirm dialog → permissions panel
// reopens) gives the admin a clear opportunity to tune the preset
// before walking away.
export const applyDepartmentPreset = (_existingCapabilities, departmentKey) => {
  const preset = DEPARTMENT_PRESETS[departmentKey];
  if (!preset) return {};
  return { ...preset };
};
